'use client';

import { Suspense } from 'react';
import { Row, TransformedNewsItem, Section, Column } from '@/lib/types/pageBuilder';
import ColumnRenderer from './ColumnRenderer';
import SectionHeading from './SectionHeading';
import { DynamicTabInfo } from './TabBarSection';
import { IncludeSectionApiData } from '@/lib/pageUtils';
import { normalizeApiEndpoint } from '@/lib/apiUtils';
interface RowRendererProps {
  row: Row;
  collectionsData: Map<string, TransformedNewsItem[]>;
  dynamicTabsMap?: Map<string, DynamicTabInfo[]>;
  includeSectionsData?: Map<string, Section>;
  includeSectionApiData?: IncludeSectionApiData;
  isLastRow?: boolean;
  parentColumnData?: TransformedNewsItem[]; // Data inherited from parent column's data_binding
  dataStartIndex?: number; // Start index for sequential data consumption
  /** Is this the first row on the page (for LCP priority) */
  isFirstRow?: boolean;
}

/**
 * Calculate how many items a component will consume
 */
function getComponentItemCount(component: {
  data_config?: {
    mode?: string;
    loop_count?: number;
  };
}): number {
  if (!component.data_config) return 1;
  if (component.data_config.mode === 'loop') {
    return component.data_config.loop_count || 1;
  }
  return 1;
}

/**
 * Calculate total items consumed by a column and its nested elements
 * Based on component loop_count (not data_binding.limit - that's for API calls)
 */
function getColumnItemCount(column: Column): number {
  let count = 0;
  if (column.components) {
    for (const comp of column.components) {
      count += getComponentItemCount(comp);
    }
  }
  if (column.columns) {
    for (const col of column.columns) {
      count += getColumnItemCount(col);
    }
  }
  if (column.rows) {
    for (const row of column.rows) {
      count += getRowItemCount(row);
    }
  }
  return count;
}

/**
 * Calculate total items consumed by a row's columns
 * Based on component loop_count (not data_binding.limit - that's for API calls)
 */
function getRowItemCount(row: {
  columns?: Column[];
}): number {
  let count = 0;
  if (row.columns) {
    for (const col of row.columns) {
      count += getColumnItemCount(col);
    }
  }
  return count;
}
export default function RowRenderer({
  row,
  collectionsData,
  dynamicTabsMap,
  includeSectionsData,
  includeSectionApiData,
  isLastRow,
  parentColumnData,
  dataStartIndex = 0,
  isFirstRow = false
}: RowRendererProps) {
  // Skip hidden rows
  if (row.hidden) {
    return null;
  }

  // Get items for this row's data binding
  // Priority: 1. This row's own data_binding (resets index), 2. Parent column's data (continues index)
  const rawRowDataBindingKey = row.data_binding?.api_endpoint || row.data_binding?.collection_id;
  // Normalize the endpoint to match how data is stored in collectionsData
  const rowDataBindingKey = rawRowDataBindingKey ? normalizeApiEndpoint(rawRowDataBindingKey) : undefined;
  const hasOwnDataBinding = !!rowDataBindingKey;
  const rawItems = rowDataBindingKey ? collectionsData.get(rowDataBindingKey) || [] : parentColumnData || [];

  // Filter out metadata items BEFORE slicing (metadata items have _isCollectionMeta flag)
  const rowItems = rawItems.filter(item => !item._isCollectionMeta);
  if (rowDataBindingKey && rowItems.length === 0) {
    console.error(`[RowRenderer] WARNING: Row ${row.id} has data_binding "${rowDataBindingKey}" but 0 items! Available keys: ${Array.from(collectionsData.keys()).join(', ')}`);
  }

  // DEBUG logging removed - was causing issues in Firefox/Safari

  // If row has its own data_binding, start from 0; otherwise continue from parent's index
  const startOffset = hasOwnDataBinding ? 0 : dataStartIndex;

  // Render columns with sequential data offsets
  let runningOffset = startOffset;

  // Check if any column has sticky — stretch containers so sticky has room to work
  const hasStickyColumn = row.columns.some(col => col.sticky);
  return <div className={hasStickyColumn ? 'flex-1 flex flex-col' : ''}>
      {/* Row heading */}
      {row.heading?.enabled && <SectionHeading heading={row.heading} />}

      <div className={`grid grid-cols-12${hasStickyColumn ? ' flex-1' : ''}`}>
        {row.columns.map((column, index) => {
        const itemCount = getColumnItemCount(column);
        // Pass priority to ALL columns in first row (LCP can be any image in first row)
        const columnPriority = isFirstRow;
        const element = <Suspense key={column.id} fallback={null}>
              <ColumnRenderer column={column} collectionsData={collectionsData} dynamicTabsMap={dynamicTabsMap} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} isLastColumn={index === row.columns.length - 1} parentColumnData={rowItems} dataStartIndex={runningOffset} priority={columnPriority} />
            </Suspense>;
        runningOffset += itemCount;
        return element;
      })}
      </div>
    </div>;
}